import React, { useEffect, useMemo } from 'react';
import { Box, Button, Divider, Text } from '@televet/kibble-ui';
import { useGetPetParentLoyaltyAccountHolderQuery } from 'shared/types/graphql';
import { RewardCard } from './RewardCard';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { useGetRewards } from '../hooks/useGetRewards';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { RewardsTab } from './RewardsPanel';

interface CurrentRewardsProps {
  accountHolderId: string;
  programId: string;
}

export const CurrentRewards = ({ accountHolderId, programId }: CurrentRewardsProps): JSX.Element => {
  const { selectedLoyaltyRewardsTab } = useSidePanelSearch();

  const {
    data: unfulfilledRewardsData,
    loading: loadingUnfulfilledRewards,
    refetch: refetchUnfulfilledRewards,
  } = useGetPetParentLoyaltyAccountHolderQuery({
    variables: {
      where: {
        id: {
          equals: accountHolderId,
        },
      },
    },
    skip: !accountHolderId,
  });

  const account = unfulfilledRewardsData?.findFirstLoyaltyAccountHolder?.account;
  const unfulfilledRewards = account?.rewardRedemptions;
  const pointBalance = account?.pointBalance;

  const unfulfilledRewardsIds = useMemo(
    () => unfulfilledRewards?.map((unfulfilledReward) => unfulfilledReward.reward.id),
    [unfulfilledRewards],
  );

  useEffect(() => {
    if (selectedLoyaltyRewardsTab === RewardsTab.CurrentRewards && unfulfilledRewards) {
      refetchUnfulfilledRewards();
    }
  }, [refetchUnfulfilledRewards, selectedLoyaltyRewardsTab, unfulfilledRewards]);

  const {
    rewards: eligibleRewards,
    loading: loadingEligibleRewards,
    rewardsCount: eligibleRewardsCount,
    fetchMore,
  } = useGetRewards(programId, {
    id: {
      notIn: unfulfilledRewardsIds,
    },
    pointCost: {
      lte: pointBalance,
    },
  });

  const triggerRefetch = (): void => {
    refetchUnfulfilledRewards();
  };

  return (
    <Box py={3}>
      {account && unfulfilledRewards && (
        <Box px={4}>
          <Text as="p" fontWeight="bold" size="lg" mb={3}>
            Unfulfilled Rewards
          </Text>
          {loadingUnfulfilledRewards ? (
            <LoadingIndicator />
          ) : !!unfulfilledRewards.length ? (
            unfulfilledRewards.map((unfulfilledReward) => (
              <Box key={unfulfilledReward.id} mb={3}>
                <RewardCard
                  reward={unfulfilledReward.reward}
                  requestedDate={unfulfilledReward.createdAt}
                  rewardRedemptionId={unfulfilledReward.id}
                  triggerRefetch={triggerRefetch}
                  accountId={account.id}
                />
              </Box>
            ))
          ) : (
            <Text size="sm" variant="subtle" as="p" textAlign="center">
              There are no unfulfilled rewards
            </Text>
          )}
        </Box>
      )}

      {unfulfilledRewards && eligibleRewards && <Divider my={5} mx={-2} />}
      {account && eligibleRewards && (
        <Box px={4}>
          <Text as="p" fontWeight="bold" size="lg" mb={3}>
            Eligible Rewards
          </Text>
          {eligibleRewards.map((eligibleReward) => {
            return (
              <Box key={eligibleReward.id} mb={3}>
                <RewardCard reward={{ ...eligibleReward }} accountId={account.id} triggerRefetch={triggerRefetch} />
              </Box>
            );
          })}
          {eligibleRewards.length !== eligibleRewardsCount && (
            <Button
              variant="ghost"
              my={2}
              isFullWidth
              size="sm"
              isLoading={loadingEligibleRewards}
              isDisabled={loadingEligibleRewards}
              onClick={(): Promise<void> => fetchMore()}
            >
              Show More
            </Button>
          )}
          {!eligibleRewards.length && (
            <Text size="sm" variant="subtle" as="p" textAlign="center">
              There are no eligible rewards
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
